import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ThemeMode = 'light' | 'dark' | 'system';

/** Tema da aplicação: light, dark ou system (segue o SO). Persiste no localStorage. */
@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly STORAGE_KEY = 'theme-preference';
  private themeSubject = new BehaviorSubject<ThemeMode>(this.getStoredTheme());
  public theme$: Observable<ThemeMode> = this.themeSubject.asObservable();

  constructor() {
    this.initializeTheme();
    this.watchSystemThemeChanges();
  }

  private initializeTheme(): void {
    const theme = this.getStoredTheme();
    this.applyTheme(theme);
  }

  private watchSystemThemeChanges(): void {
    if (window.matchMedia) {
      const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');

      darkModeQuery.addEventListener('change', (e) => {
        if (this.themeSubject.value === 'system') {
          this.applyTheme('system');
        }
      });
    }
  }

  private getStoredTheme(): ThemeMode {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return (stored as ThemeMode) || 'system';
  }

  private storeTheme(theme: ThemeMode): void {
    localStorage.setItem(this.STORAGE_KEY, theme);
  }

  private getEffectiveTheme(theme: ThemeMode): 'light' | 'dark' {
    if (theme === 'system') {
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
      return 'light';
    }
    return theme;
  }

  private applyTheme(theme: ThemeMode): void {
    const effectiveTheme = this.getEffectiveTheme(theme);
    const htmlElement = document.documentElement;
    // TailwindCSS dark mode só funciona com a classe 'dark' presente
    if (effectiveTheme === 'dark') {
      htmlElement.classList.add('dark');
      // Aplica também ao overlay do Angular Material
      const overlayContainer = document.querySelector('.cdk-overlay-container');
      if (overlayContainer) {
        overlayContainer.classList.add('dark');
      }
    } else {
      htmlElement.classList.remove('dark');
      const overlayContainer = document.querySelector('.cdk-overlay-container');
      if (overlayContainer) {
        overlayContainer.classList.remove('dark');
      }
    }
    this.themeSubject.next(theme);
  }

  setTheme(theme: ThemeMode): void {
    this.storeTheme(theme);
    this.applyTheme(theme);
  }

  getCurrentTheme(): ThemeMode {
    return this.themeSubject.value;
  }

  /** Tema efetivo aplicado (resolve 'system' para light/dark). */
  getEffectiveCurrentTheme(): 'light' | 'dark' {
    return this.getEffectiveTheme(this.themeSubject.value);
  }

  toggleTheme(): void {
    const current = this.themeSubject.value;
    const nextTheme: ThemeMode = current === 'light' ? 'dark' : 'light';
    this.setTheme(nextTheme);
  }
}
